import { ScaleTime, ScaleLinear, ScaleBand } from "d3"
import { DataSource } from "./DataSource"
import { ColorSpectrum } from "./ColorSpectrum"
import { Analytic, getQEEGDSARequestAnalysis, OnDemandAnalysis, QEEGDSAAnalysis } from "./AnalysisDetails"
import { ColorRGB } from "./Color"
import { SummaryValueConfig } from "./Graph"

export type DSADisplayScale = "power" | "voltage" | "sqrt_voltage"
export const DEFAULT_DSA_DISPLAY_SCALE: DSADisplayScale = "sqrt_voltage"
export const DSA_FREQUENCY_LABELS: string[] = Array.from({ length: 40 }, (_, index) => ((index * 0.5) + 0.25).toFixed(2))

export type BaseTraceConfigJSON = {
	id: string
	dataKey: string
	dataSource: DataSource
	name: string
	units: string
	rawDataKey: string
	onDemandAnalysis?: OnDemandAnalysis
	summaryValueConfig?: SummaryValueConfig
	isVisible?: boolean // default to true if undefined
}

// Some traces are part of composite data types. EEG, CPPOPT, etc.
// So, we need to provide the index of the composite data that they belong to.
export type CompositePartTraceConfigJSON = BaseTraceConfigJSON & {
	isCompositePart: true
	compositeIndex: number
}

// If the trace is not a composite part, then it does not have a composite index.
export type NonCompositePartTraceConfig = BaseTraceConfigJSON & {
	isCompositePart: false
}

export type TraceDataConfig = CompositePartTraceConfigJSON | NonCompositePartTraceConfig

export type SolidColorTraceDataConfig = TraceDataConfig & WithSolidColor

export type TraceConfigJSON = LineTraceConfigJSON | HeatmapTraceConfigJSON | SpectrogramTraceConfigJSON | DeltaRegionJSON

export type TraceConfigJSONWithDataObjectId = (TraceConfigJSON & { dataObjectId: number }) | DeltaRegionWithDataObjectId

type DeltaRegionWithDataObjectId = DeltaRegion & {
	first: BaseTraceConfig & TraceDataConfig & { dataObjectId: number }
	second: BaseTraceConfig & TraceDataConfig & { dataObjectId: number }
}

export type TraceConfig = LineTraceConfig | HeatmapTraceConfig | SpectrogramTraceConfig | DeltaRegion

export type BaseTraceConfig = {
	graphId: string
	xScale: ScaleTime<any, any, any>
	isVisible?: boolean
}

export type LineTraceConfig = LineTraceConfigJSON & BaseTraceConfig & {
	yScale: ScaleLinear<any, any, any>
}

export type HeatmapTraceConfig = HeatmapTraceConfigJSON & BaseTraceConfig & {
	yScale: ScaleBand<any>
}

export type PageRectangle = {
	x: number
	y: number
	width: number
	height: number
}

export enum RenderStrategy {
	LINE = "line",
	HEATMAP = "heatmap",
	IMAGE = "image",
	DELTA = "delta",
	SPECTROGRAM = "spectrogram"
}

type BaseTraceConfigSeed = Pick<BaseTraceConfigJSON, "name" | "dataKey" | "rawDataKey" | "dataSource" | "units" | "onDemandAnalysis">

export type TraceConfigSeed = BaseTraceConfigSeed & (
	| { isCompositePart: true; compositeIndex: number }
	| { isCompositePart: false }
)

export type TraceOption = {
	label: string
	source: string
	value: TraceConfigSeed
	supportedRenderStrategies: RenderStrategy[]
	highlight: boolean
}

export const isQEEGDSASourceModality = (rawDataKey: string): boolean => rawDataKey.startsWith("qEEG_DSA")

export const isQEEGDSAAnalysis = (analysis: OnDemandAnalysis | undefined): analysis is QEEGDSAAnalysis => (
	analysis?.analytic === Analytic.QEEG_DSA
)

export const getSpectrogramDataKey = (rawDataKey: string, analysis: OnDemandAnalysis | undefined): string => (
	isQEEGDSAAnalysis(analysis)
		? JSON.stringify(getQEEGDSARequestAnalysis(analysis))
		: rawDataKey
)

export const getSupportedRenderStrategies = (rawDataKey: string): RenderStrategy[] => (
	isQEEGDSASourceModality(rawDataKey)
		? []
		: [RenderStrategy.LINE, RenderStrategy.HEATMAP]
)

export type DeltaRegionJSON = {
	id: string
	renderStrategy: RenderStrategy.DELTA
    first: BaseTraceConfigJSON & TraceDataConfig
	second: BaseTraceConfigJSON & TraceDataConfig
	color: ColorRGB
	name: string
	summaryValueConfig?: SummaryValueConfig
	isVisible?: boolean
}

export type DeltaRegion = DeltaRegionJSON & BaseTraceConfig & {
	first: BaseTraceConfig & TraceDataConfig
	second: BaseTraceConfig & TraceDataConfig
	yScale: ScaleLinear<any, any, any>
}

export type LineTraceConfigJSON = TraceDataConfig & WithSolidColor & {
	renderStrategy: RenderStrategy.LINE
	color: string
}

export type HeatmapTraceConfigJSON = TraceDataConfig & WithColorSpectrum & {
	renderStrategy: RenderStrategy.HEATMAP
	lowerBound: number
	upperBound: number
}

export const getHeatmapTraceId = (config: HeatmapTraceConfigJSON) => config.id

export type SpectrogramTraceConfigJSON = TraceDataConfig & WithColorSpectrum & {
	renderStrategy: RenderStrategy.SPECTROGRAM
	displayScale: DSADisplayScale
	lowerBound: number
	upperBound: number
	frequencyLabels: string[]
}

export type SpectrogramTraceConfig = SpectrogramTraceConfigJSON & BaseTraceConfig & {
	yScale: ScaleBand<string>
}

export const getSpectrogramTraceId = (config: SpectrogramTraceConfigJSON) => config.id

export type WithSolidColor = { color: string }

export type WithColorSpectrum = { colorSpectrum: ColorSpectrum }

export const getFullTraceDataConfigs = (trace: TraceConfigJSON | TraceConfigJSONWithDataObjectId): (BaseTraceConfigJSON & TraceDataConfig)[] => {
	// Because of delta regions (which have 2 trace configurations) we need to be able to cleanly 
	// get the information we need to request data from the backend. This happens pretty often.

	switch (trace.renderStrategy) {
		case RenderStrategy.DELTA:
			return [trace.first, trace.second]
		default:
			return [trace]
	}
}

export type UpdateTraceOptions = {
	useModalitySpecificDefaults: boolean
}
